/***************************************************************************
 * ========================================================================
 * Copyright (c) 2026 Broadcom Inc. and/or its subsidiaries. All Rights Reserved. Broadcom Confidential.
 * ========================================================================
 */

/** @module MessageItemsModule */

import { IClientInsightsSampling } from 'generated-types';
import { ClientInsightsSampling } from 'object-types';
import { MessageItem } from 'ajs/modules/data-model/factories/message-item.factory';

import {
    IpAddrMatchConfigItem,
} from 'ajs/modules/match/factories/ip-addr-match.config-item.factory';

import {
    StringMatchConfigItem,
} from 'ajs/modules/match/factories/string-match.config-item.factory';

/**
 * Partial type of Client Insights Sampling object.
 */
type clientInsightsSamplingPartial = Omit<IClientInsightsSampling,
'client_ip' |
'sample_uris' |
'skip_uris'
>;

/**
 * Client Insights Sampling Config type.
 */
interface IClientInsightsSamplingConfig extends clientInsightsSamplingPartial {
    client_ip?: IpAddrMatchConfigItem;
    sample_uris?: StringMatchConfigItem;
    skip_uris?: StringMatchConfigItem;
}

/**
 * @description
 *
 *   Client Insights Sampling Message Item.
 *
 * @author Sarthak kapoor
 */
export class ClientInsightsSamplingConfigItem extends MessageItem<IClientInsightsSamplingConfig> {
    constructor(args = {}) {
        const extendedArgs = {
            objectType: ClientInsightsSampling,
            ...args,
        };

        super(extendedArgs);
    }

    /**
     * Safeset sample_uris field on selection change.
     */
    public setSampleUris(): void {
        if (!this.config.sample_uris) {
            this.safeSetNewChildByField('sample_uris');
        }

        this.removeSkipUrisMatch();
    }

    /**
     * Safeset skip_uris field on selection change.
     */
    public setSkipUris(): void {
        if (!this.config.skip_uris) {
            this.safeSetNewChildByField('skip_uris');
        }

        this.removeSampleUrisMatch();
    }

    /**
     * Safeset client_ip field on selection change.
     */
    public setClientIp(): void {
        if (!this.config.client_ip) {
            this.safeSetNewChildByField('client_ip');
        }
    }

    /**
     * Remove all the matches from the config.
     */
    public removeAllMatches(): void {
        this.removeClientIpMatch();
        this.removeSampleUrisMatch();
        this.removeSkipUrisMatch();
    }

    /** @override */
    protected requiredFields(): string[] {
        return [
            'client_ip',
            'sample_uris',
            'skip_uris',
        ];
    }

    /**
     * Remove Client IP match from the config.
     */
    private removeClientIpMatch(): void {
        this.config.client_ip = undefined;
    }

    /**
     * Remove Skip Uris match from the config.
     */
    private removeSkipUrisMatch(): void {
        this.config.skip_uris = undefined;
    }

    /**
     * Remove Sample Uris match from the config.
     */
    private removeSampleUrisMatch(): void {
        this.config.sample_uris = undefined;
    }
}
